import { SheetOverrides } from "@/modes/mode-selectors";
import { selectIElementsSlice } from "@/store/ielements-slice-selector";
import { isLightweightLocationMarker } from "@/store/lightweight-elements-discriminators";
import {
  selectLightweightChild,
  selectLightweightDirectChildren,
  selectLightweightElementsSlice,
} from "@/store/lightweight-elements-selectors";
import {
  LightweightElement,
  LightweightElementsRecord,
  LightweightElementsState,
} from "@/store/lightweight-elements-slice";
import { curryAppSelector } from "@/store/reselect";
import { RootState } from "@/store/store";
import { selectActiveTool } from "@/store/ui/ui-selectors";
import { ToolName, type OnlyUiState } from "@/store/ui/ui-slice";
import {
  GUID,
  IElement,
  IElementType,
  IElementTypeHint,
  isIElementGenericImgSheet,
} from "@faro-lotv/ielement-types";
import { TypeAndHint, selectIElement } from "@faro-lotv/project-source";
import { createSelector } from "@reduxjs/toolkit";
import type { OnlyPdfReportStore } from "./pdf-report-slice";

/**
 * @returns The list of video walk dataset that has not been loaded yet
 * (i.e. the element can be found in the LightWeight Elements slice but the
 * descendants 360 images)
 * @param state The current app state
 * @param selectedIds The list of (leaf) elements selected by the user
 */
export const selectVideoWalksToLoad = curryAppSelector(
  createSelector(
    [
      selectLightweightElementsSlice,
      selectIElementsSlice,
      (state: RootState, selectedIds: GUID[]) => selectedIds,
    ],
    (lightweightState, iElementsState, selectedIds) => {
      const videoWalks = [];
      for (const lightweightId of selectedIds) {
        const lightweight = lightweightState.lightweightElements[lightweightId];
        if (
          !lightweight ||
          lightweight.type !== IElementType.section ||
          lightweight.typeHint !== IElementTypeHint.dataSession
        ) {
          continue;
        }

        const lightweightVideoMode = selectLightweightChild(
          lightweight,
          (e): e is LightweightElement =>
            e.type === IElementType.section &&
            e.typeHint === IElementTypeHint.odometryPath,
        )({ lightweightElements: lightweightState });
        if (!lightweightVideoMode) continue;

        const videoMode = selectIElement(lightweightVideoMode.id)({
          iElements: iElementsState,
        });
        if (videoMode) continue;

        videoWalks.push(lightweightVideoMode.id);
      }
      return videoWalks;
    },
  ),
);

/**
 * @returns The list of iElements that should completely hidden in the UI, by
 * detaching them from the store
 * @param state The current app state
 */
export const selectElementsToHide = createSelector(
  [selectLightweightElementsSlice],
  (lightweightElementsState: LightweightElementsState) => {
    const { lightweightElements, hiddenElements } = lightweightElementsState;
    const elementsToHide: TypeAndHint[] = [
      [IElementType.group, IElementTypeHint.unplacedUploads],
      [IElementType.group, IElementTypeHint.bimModel],
      [IElementType.cluster, IElementTypeHint.siteScapeCapture],
    ];

    const hidden: GUID[] = [];
    for (const [id, iElement] of Object.entries(lightweightElements)) {
      if (!iElement && !hiddenElements.find((e) => e.id === id)) continue;
      if (!iElement) {
        hidden.push(id);
        continue;
      }
      if (
        elementsToHide.some(
          ([type, hint]) =>
            iElement.type === type && iElement.typeHint === hint,
        ) ||
        isLightweightLocationMarker(iElement, lightweightElements) ||
        isUnsupportedDataSession(iElement, lightweightElements, false)
      ) {
        hidden.push(iElement.id);
      }

      if (
        iElement.type === IElementType.section &&
        iElement.typeHint === IElementTypeHint.dataSetCapture
      ) {
        if (isUnsupportedDataSetCapture(iElement, lightweightElementsState)) {
          hidden.push(iElement.id);
        }
      }
    }
    return hidden;
  },
);

function isUnsupportedDataSetCapture(
  iElement: LightweightElement,
  lightweightElements: LightweightElementsState,
): boolean {
  const rootCluster = selectLightweightChild(
    iElement,
    (el): el is LightweightElement =>
      el.type === IElementType.cluster &&
      el.typeHint === IElementTypeHint.rootCluster,
  )({ lightweightElements });

  const rootChildren = selectLightweightDirectChildren(
    rootCluster,
    () => true,
  )({ lightweightElements });

  // The root cluster keeps supported scan sections directly, so an empty
  // list of children means there is nothing unsupported to hide
  if (!rootChildren.length) return false;

  // Hide the capture only when every scan under the root cluster is an
  // unsupported SiteScape cluster/scan
  return rootChildren.every(
    (child) =>
      child.typeHint === IElementTypeHint.siteScapeCapture ||
      child.typeHint === IElementTypeHint.siteScapeScan,
  );
}

function isUnsupportedDataSession(
  iElement: IElement,
  lightweightElements: LightweightElementsRecord,
  hideVideoWalks: boolean,
): boolean {
  if (
    iElement.type !== IElementType.section ||
    iElement.typeHint !== IElementTypeHint.dataSession
  ) {
    return false;
  }
  if (!iElement.childrenIds?.length) return true;

  // We should hide all data sessions that do not contain a
  // videomode dataset
  for (const childId of iElement.childrenIds) {
    const child = lightweightElements[childId];
    if (!child) continue;

    if (
      child.type === IElementType.section &&
      child.typeHint === IElementTypeHint.dataSetVideoWalk &&
      !hideVideoWalks
    ) {
      // If the dataset contains only one child, it means that it was not processed correctly
      const has360 = child.childrenIds && child.childrenIds.length > 1;
      return !has360;
    }
  }

  return true;
}

/**
 * @returns Sheet overrides for the current tool, or undefined if no overrides are needed.
 * In PDF report mode, all generic image sheets (including tiled) are allowed and the user's per-area sheet selection is applied.
 * @param state The current app state
 */
export function selectSheetOverrides(
  state: OnlyUiState & OnlyPdfReportStore,
): SheetOverrides | undefined {
  if (selectActiveTool(state) !== ToolName.createPDFReport) return undefined;
  return {
    sheetFilter: (s) => isIElementGenericImgSheet(s),
    activeSheetByArea: state.pdfReport.selectedSheetByArea,
  };
}
